import TableFooter from 'Components/Molecules/TableFooter/TableFooter';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTable } from 'Services/Table/TableContext';
import styles from './TableFixed.module.sass';

const TableFixed = ({
    header, children
}: any) => {
    const scroller = useRef<any>(null);
    const { state: { pageNumber } } = useTable();

    useEffect(() => {
        if (!scroller) return;
        window.scrollTo(0, 0);
    }, [pageNumber]);

    const contentRef = useRef<any>(null);
    const scrollbarTrackRef = useRef<any>(null);
    const scrollbarThumbRef = useRef<any>(null);
    const [scrollbarState, setScrollbarState] = useState({
        thumbWidth: 0,

        scrollStartPosition: 0,
        initialScrollLeft: 0,
        isDragging: false,

        scrollLeft: 0,
    });

    useEffect(() => {
        if (contentRef?.current && scrollbarTrackRef?.current) {
            const { clientWidth: trackSize } = scrollbarTrackRef.current;
            const { clientWidth: contentSize, scrollWidth: contentScrollSize } = contentRef.current;

            setScrollbarState((state) => ({
                ...state,
                thumbWidth: Math.max((contentSize / contentScrollSize) * trackSize, 20)
            }));

            const ref = contentRef.current;
            ref.addEventListener('scroll', handleThumbPosition);

            return () => {
                ref.removeEventListener('scroll', handleThumbPosition);
            }
        }
    }, [
        contentRef?.current?.scrollWidth,
        scrollbarTrackRef?.current,
        window?.innerWidth
    ]);

    const handleThumbPosition = useCallback(() => {
        if (!contentRef.current
            || !scrollbarTrackRef.current
            || !scrollbarThumbRef.current
        ) return;

        const { scrollLeft: contentLeft, scrollWidth: contentScrollWidth } = contentRef.current;
        const { clientWidth: trackWidth } = scrollbarTrackRef.current;
        let newLeft = (+contentLeft / +contentScrollWidth) * trackWidth;
        newLeft = Math.min(newLeft, trackWidth - scrollbarState.thumbWidth);
        scrollbarThumbRef.current.style.left = `${newLeft}px`;
    }, []);

    const handleThumbMouseDown = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();

        setScrollbarState((state) => ({
            ...state,
            scrollStartPosition: e.clientX,
            initialScrollLeft: contentRef.current?.scrollLeft,
            isDragging: true
        }));
    }, []);

    const handleThumbMouseup = useCallback(
        (e) => {
            if (scrollbarState.isDragging) {
                e.preventDefault();
                e.stopPropagation();
                setScrollbarState((state) => ({
                    ...state,
                    isDragging: false
                }));
            }
        },
        [scrollbarState.isDragging]
    );

    const handleThumbMouseMove = useCallback((e) => {
        if (scrollbarState.isDragging) {
            e.preventDefault();
            e.stopPropagation();

            const {
                scrollWidth: contentScrollWidth,
                offsetWidth: contentOffsetWidth
            } = contentRef.current;

            const deltaY = (e.clientX - scrollbarState.scrollStartPosition) * (contentOffsetWidth / scrollbarState.thumbWidth);
            const newScrollLeft = Math.min(scrollbarState.initialScrollLeft + deltaY, contentScrollWidth - contentOffsetWidth);

            contentRef.current.scrollLeft = newScrollLeft;
            setScrollbarState((state) => ({
                ...state,
                scrollLeft: newScrollLeft
            }));
        }
    }, [scrollbarState.isDragging, scrollbarState.scrollStartPosition, scrollbarState.thumbWidth]);

    useEffect(() => {
        document.addEventListener('mousemove', handleThumbMouseMove);
        document.addEventListener('mouseup', handleThumbMouseup);
        document.addEventListener('mouseleave', handleThumbMouseup);

        return () => {
          document.removeEventListener('mousemove', handleThumbMouseMove);
          document.removeEventListener('mouseup', handleThumbMouseup);
          document.removeEventListener('mouseleave', handleThumbMouseup);
        };
      }, [handleThumbMouseMove, handleThumbMouseup]);

    return <div className={styles['tablefixed']}>
        { React.cloneElement(header, {
            scrollLeft: scrollbarState?.scrollLeft
        }) }
        <div
            ref={contentRef}
            className={styles['tablefixed__content']}
        >
            { children }
        </div>
        {
            scrollbarState?.scrollLeft >= 32 &&
            <div className={styles['tablefixed__indicator--left']} />
        }
        {
            scrollbarState?.scrollLeft <= (contentRef?.current?.scrollWidth - contentRef?.current?.clientWidth - 32) &&
            <div className={styles['tablefixed__indicator--right']} />
        }

        {
            contentRef?.current?.scrollWidth > contentRef?.current?.clientWidth &&
            <div
                className={styles['tablefixed__scrollbar']}
                ref={scrollbarTrackRef}
            >
                <div
                    className={styles['tablefixed__scrollbar-thumb']}
                    ref={scrollbarThumbRef}
                    onMouseDown={handleThumbMouseDown}
                    style={{
                        position: 'relative',
                        width: scrollbarState.thumbWidth + 'px'
                    }}
                />
            </div>
        }

        <TableFooter />
    </div>
}

export default TableFixed;